// Wave 20.14 — /u/:username — public rider profile.
//
// Renders display name, username, follower/following counts, follow
// button, and a list of the user's activities (visibility-gated).
// Anyone can view (no auth) — followers see follower-only rows.

import React, { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import DirectionsBikeIcon from '@mui/icons-material/DirectionsBike';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import { Link as RouterLink, useParams } from 'react-router-dom';
import PageLayout from './components/PageLayout';
import FollowButton from './components/FollowButton';
import { colors } from './theme';
import {
  fmtDistance,
  fmtElevation,
  fmtSpeed,
  useUnitSystem,
} from './services/useUnitSystem';

const API = process.env.REACT_APP_API || 'https://api.aero.chat';
const TOKEN_KEY = process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token';

interface Profile {
  id: number;
  username: string;
  displayName: string;
  followerCount: number;
  followingCount: number;
}

interface ActivityRow {
  id: string;
  source: string;
  type: 'indoor_aero' | 'outdoor_cycling' | 'outdoor_running';
  started_at: string;
  ended_at: string | null;
  distance_m: number | null;
  elevation_gain_m: number | null;
  avg_speed_mps: number | null;
  max_speed_mps: number | null;
  best_cda: number | null;
  best_position: string | null;
  visibility: 'public' | 'followers' | 'private';
}

async function fetchJson<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const jwt = localStorage.getItem(TOKEN_KEY);
  const res = await fetch(`${API}${path}`, {
    ...opts,
    headers: { ...(opts.headers || {}), ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}) },
  });
  if (!res.ok) throw new Error(String(res.status));
  return res.json();
}

export default function UserProfile() {
  const { username } = useParams<{ username: string }>();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [activities, setActivities] = useState<ActivityRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!username) return;
    setLoading(true);
    setError(null);
    Promise.all([
      fetchJson<Profile | null>(`/users/by-username/${encodeURIComponent(username)}`),
      fetchJson<ActivityRow[]>(`/users/by-username/${encodeURIComponent(username)}/activities`).catch(() => []),
    ])
      .then(([p, acts]) => {
        setProfile(p);
        setActivities(acts);
      })
      .catch((e) => setError(e?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [username]);

  if (loading) {
    return (
      <PageLayout maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: colors.brand }} />
        </Box>
      </PageLayout>
    );
  }
  if (error || !profile) {
    return (
      <PageLayout maxWidth="sm">
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>User not found</Typography>
          <Typography sx={{ color: colors.textMuted, mt: 1 }}>
            @{username} doesn't exist on aero.chat.
          </Typography>
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout maxWidth="md">
      <Stack spacing={3}>
        <Box
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: 3,
            backgroundColor: colors.surface,
            border: `1px solid ${colors.border}`,
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 900 }}>
                {profile.displayName}
              </Typography>
              <Typography sx={{ color: colors.textMuted, fontSize: '0.95rem' }}>@{profile.username}</Typography>
              <Stack direction="row" spacing={3} sx={{ mt: 2 }}>
                <StatMini label="Followers" value={profile.followerCount} />
                <StatMini label="Following" value={profile.followingCount} />
                <StatMini label="Sessions" value={activities.length} />
              </Stack>
            </Box>
            <FollowButton userId={profile.id} />
          </Stack>
        </Box>

        <Box>
          <Typography variant="overline" sx={{ color: colors.brand, letterSpacing: '0.2em' }}>
            Activities
          </Typography>
          {activities.length === 0 ? (
            <Typography sx={{ color: colors.textMuted, fontStyle: 'italic', mt: 1 }}>
              No public activities yet.
            </Typography>
          ) : (
            <Stack spacing={1} sx={{ mt: 1 }}>
              {activities.map((a) => (
                <ActivityRowCard key={a.id} row={a} />
              ))}
            </Stack>
          )}
        </Box>
      </Stack>
    </PageLayout>
  );
}

function StatMini({ label, value }: { label: string; value: number }) {
  return (
    <Box>
      <Typography sx={{ fontWeight: 900, fontSize: '1.4rem' }}>{value}</Typography>
      <Typography sx={{ color: colors.textMuted, fontSize: '0.75rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
        {label}
      </Typography>
    </Box>
  );
}

function ActivityRowCard({ row }: { row: ActivityRow }) {
  const units = useUnitSystem();
  const Icon =
    row.type === 'outdoor_running'
      ? DirectionsRunIcon
      : row.type === 'outdoor_cycling'
      ? DirectionsBikeIcon
      : CameraAltIcon;
  // Wave 20.40 — unified /sessions/:id for both types.
  const href = `/sessions/${row.id}`;
  return (
    <RouterLink to={href} style={{ textDecoration: 'none', color: 'inherit' }}>
      <Box
        sx={{
          p: 2,
          borderRadius: 2,
          backgroundColor: colors.surface,
          border: `1px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          transition: 'border-color 120ms',
          '&:hover': { borderColor: colors.brand },
        }}
      >
        <Icon sx={{ color: colors.brand, fontSize: 26 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography sx={{ fontWeight: 700 }}>
              {row.type === 'indoor_aero' ? 'Aero session' : row.type === 'outdoor_running' ? 'Run' : 'Ride'}
            </Typography>
            {row.visibility === 'followers' && (
              <Chip
                label="FRIENDS"
                size="small"
                sx={{ backgroundColor: `${colors.brand}22`, color: colors.brand, fontWeight: 800, height: 18, fontSize: '0.6rem' }}
              />
            )}
            <Typography sx={{ color: colors.textMuted, fontSize: '0.8rem' }}>
              {new Date(row.started_at).toLocaleDateString()}
            </Typography>
          </Stack>
          {row.type !== 'indoor_aero' ? (
            <Typography sx={{ color: colors.textMuted, fontSize: '0.85rem', mt: 0.25 }}>
              {row.distance_m ? fmtDistance(row.distance_m, units) : '—'}
              {row.avg_speed_mps ? ` · ${fmtSpeed(row.avg_speed_mps, units)}` : ''}
              {row.elevation_gain_m ? ` · ${fmtElevation(row.elevation_gain_m, units)} gain` : ''}
            </Typography>
          ) : (
            <Typography sx={{ color: colors.textMuted, fontSize: '0.85rem', mt: 0.25 }}>
              {row.best_cda != null ? `Best CdA ${row.best_cda.toFixed(3)}` : ''}
              {row.best_position ? ` · ${row.best_position}` : ''}
            </Typography>
          )}
        </Box>
      </Box>
    </RouterLink>
  );
}
